import React, { useEffect, useRef, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { api } from '../lib/api'
import { useCountdown } from '../lib/useCountdown'

const CATS = [
  { key: 'mono', label: 'Mono' },
  { key: 'colour', label: 'Colour' },
  { key: 'themed', label: 'Themed' },
] as const

const ENTRY_GUIDANCE =
  'Each image is submitted as soon as you click Save. You can enter 1, 2 or 3 images, depending on which categories you want to enter.'

type Entry = { id:number, category:'mono'|'colour'|'themed', title:string, fileKey:string|null, previewUrl?:string }
type LoadResp = {
  competition: { title:string, closesAt:string, slug:string, status:string }
  submission: { competitionNumber:string, className:'Novice'|'Intermediate'|'Advanced', email:string }
  entries: Entry[]
  locked: boolean
}

type EntryArrivalState = {
  arrivedFromEntry?: boolean
  emailSent?: boolean
  submissionEmail?: string
}

function getSaveValidationMessage(title: string, file: File | null, hasExistingUpload: boolean) {
  if (!title.trim()) return 'Please enter an image title before saving.'
  if (!hasExistingUpload && !file) return 'Please choose a JPG image before saving.'
  return ''
}

export default function CompetitionEdit() {
  const { slug, token } = useParams()
  const location = useLocation()
  const [data, setData] = useState<LoadResp | null>(null)
  const [titles, setTitles] = useState<Record<string,string>>({})
  const [files, setFiles] = useState<Record<string, File | null>>({})
  const [isEditing, setIsEditing] = useState<Record<string, boolean>>({})
  const [confirmDeleteByCat, setConfirmDeleteByCat] = useState<Record<string, boolean>>({})
  const [postingByKey, setPostingByKey] = useState<Record<string, boolean>>({})
  const [postedByKey, setPostedByKey] = useState<Record<string, boolean>>({})
  const [saveErrByCat, setSaveErrByCat] = useState<Record<string, string>>({})
  const [err, setErr] = useState('')
  const [ok, setOk] = useState('')
  const postResetTimers = useRef<Record<string, number>>({})
  const countdown = useCountdown(data?.competition?.closesAt || null)
  const arrivalState = (location.state as EntryArrivalState | null) || null
  const firstArrivalEmail = arrivalState?.arrivedFromEntry && arrivalState?.emailSent && arrivalState?.submissionEmail
    ? arrivalState.submissionEmail
    : ''

  function setPosting(key: string, value: boolean) {
    setPostingByKey(prev => ({ ...prev, [key]: value }))
  }

  function setPosted(key: string) {
    if (postResetTimers.current[key]) window.clearTimeout(postResetTimers.current[key])
    setPostedByKey(prev => ({ ...prev, [key]: true }))
    postResetTimers.current[key] = window.setTimeout(() => {
      setPostedByKey(prev => ({ ...prev, [key]: false }))
      delete postResetTimers.current[key]
    }, 1500)
  }

  async function load() {
    setErr(''); setOk('')
    try {
      const d = await api<LoadResp>(`/api/competitions/${slug}/edit/${token}`)
      setData(d)
      const t: Record<string,string> = {}
      const nextEditing: Record<string, boolean> = {}
      for (const cat of CATS) t[cat.key] = d.entries.find(e=>e.category===cat.key)?.title || ''
      for (const cat of CATS) nextEditing[cat.key] = !d.entries.some(e => e.category === cat.key)
      setTitles(t)
      setIsEditing(nextEditing)
      setConfirmDeleteByCat({})
    } catch (e:any) { setErr(e.message) }
  }

  useEffect(()=>{ load() }, [slug, token])
  useEffect(() => {
    return () => {
      for (const timer of Object.values(postResetTimers.current)) window.clearTimeout(timer)
    }
  }, [])

  async function saveCategory(cat: string, hasExistingUpload: boolean) {
    setErr(''); setOk('')
    setSaveErrByCat(prev => ({ ...prev, [cat]: '' }))
    setConfirmDeleteByCat(prev => ({ ...prev, [cat]: false }))
    if (!data) return
    const validationMessage = getSaveValidationMessage(titles[cat] || '', files[cat] || null, hasExistingUpload)
    if (validationMessage) {
      setSaveErrByCat(prev => ({ ...prev, [cat]: validationMessage }))
      return
    }
    const fd = new FormData()
    fd.set('title', titles[cat] || '')
    const f = files[cat]
    if (f) fd.set('file', f)
    const key = `save-${cat}`
    try {
      setPosting(key, true)
      await Promise.all([
        api<{ok:true}>(`/api/competitions/${slug}/edit/${token}/entry/${cat}`, { method:'POST', body: fd }),
        new Promise(resolve => setTimeout(resolve, 450))
      ])
      setOk('Saved.')
      setIsEditing(prev => ({ ...prev, [cat]: false }))
      setFiles(prev => ({...prev, [cat]: null}))
      await load()
      setPosted(key)
    } catch (e:any) {
      setSaveErrByCat(prev => ({ ...prev, [cat]: e.message || 'Could not save this entry.' }))
    } finally {
      setPosting(key, false)
    }
  }

  async function deleteCategory(cat: string) {
    setErr(''); setOk('')
    setSaveErrByCat(prev => ({ ...prev, [cat]: '' }))
    if (!data) return
    const key = `delete-${cat}`
    try {
      setPosting(key, true)
      await Promise.all([
        api<{ok:true}>(`/api/competitions/${slug}/edit/${token}/entry/${cat}`, { method:'DELETE' }),
        new Promise(resolve => setTimeout(resolve, 450))
      ])
      setOk('Entry deleted.')
      setFiles(prev => ({ ...prev, [cat]: null }))
      await load()
    } catch (e:any) {
      setSaveErrByCat(prev => ({ ...prev, [cat]: e.message || 'Could not delete this entry.' }))
    } finally {
      setPosting(key, false)
    }
  }

  function renderEntryPreview(catLabel: string, entry: Entry | undefined, variant: 'inline' | 'block') {
    if (!entry?.previewUrl) return null
    return (
      <a
        href={entry.previewUrl}
        target="_blank"
        rel="noreferrer"
        className={`entry-thumb-link${variant === 'inline' ? ' entry-thumb-link-inline' : ''}`}
        aria-label={`Open ${catLabel} image in a new tab`}
      >
        <img
          src={entry.previewUrl}
          alt={entry.title || `${catLabel} entry`}
          className={`entry-thumb-image${variant === 'inline' ? ' entry-thumb-image-inline' : ''}`}
          loading="lazy"
        />
      </a>
    )
  }

  function renderEntryFields(catKey: (typeof CATS)[number]['key'], entry: Entry | undefined) {
    if (!data) return null
    const saveValidationMessage = getSaveValidationMessage(
      titles[catKey] || '',
      files[catKey] || null,
      Boolean(entry?.previewUrl)
    )
    const canSave = !data.locked && !saveValidationMessage
    return (
      <>
        <div style={{marginTop:30}}>
          <label>Image title</label>
          <input
            value={titles[catKey] || ''}
            onChange={e=>{
              setTitles(prev=>({...prev, [catKey]: e.target.value}))
              setSaveErrByCat(prev => ({ ...prev, [catKey]: '' }))
            }}
            disabled={data.locked}
            placeholder="My image title"
            style={{width:'100%'}}
          />
        </div>

        <div style={{marginTop:10}}>
          <label>Upload JPG (max 10MB)</label>
          <input
            type="file"
            accept="image/jpeg"
            disabled={data.locked}
            onChange={e=>{
              setFiles(prev=>({...prev, [catKey]: e.target.files?.[0] || null}))
              setSaveErrByCat(prev => ({ ...prev, [catKey]: '' }))
            }}
          />
        </div>
        {saveErrByCat[catKey] && <p className='error-text' style={{marginTop:10}}>{saveErrByCat[catKey]}</p>}

        <div className="row" style={{marginTop:12}}>
          <span onClick={() => {
            if (saveValidationMessage) setSaveErrByCat(prev => ({ ...prev, [catKey]: saveValidationMessage }))
          }}>
            <button
              className="primary"
              onClick={()=>saveCategory(catKey, Boolean(entry?.previewUrl))}
              disabled={!canSave || Boolean(postingByKey[`save-${catKey}`])}
              title={saveValidationMessage || undefined}
            >
              {postingByKey[`save-${catKey}`] ? (
                <span className="row" style={{gap:8}}>
                  <span className="btn-spinner" />
                  <span>Saving...</span>
                </span>
              ) : postedByKey[`save-${catKey}`] ? 'Saved' : 'Save'}
            </button>
          </span>
          {entry && !data.locked && !confirmDeleteByCat[catKey] && (
            <button
              type="button"
              onClick={() => setConfirmDeleteByCat(prev => ({ ...prev, [catKey]: true }))}
              disabled={Boolean(postingByKey[`save-${catKey}`]) || Boolean(postingByKey[`delete-${catKey}`])}
            >
              Delete entry
            </button>
          )}
        </div>
        {entry && !data.locked && confirmDeleteByCat[catKey] && (
          <div className="entry-delete-confirm">
            <small className="muted">Delete this entry? This removes the image and title.</small>
            <div className="row">
              <button
                type="button"
                onClick={() => setConfirmDeleteByCat(prev => ({ ...prev, [catKey]: false }))}
                disabled={Boolean(postingByKey[`delete-${catKey}`])}
              >
                Cancel
              </button>
              <button
                type="button"
                className="danger"
                onClick={() => deleteCategory(catKey)}
                disabled={Boolean(postingByKey[`delete-${catKey}`]) || Boolean(postingByKey[`save-${catKey}`])}
              >
                {postingByKey[`delete-${catKey}`] ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        )}
      </>
    )
  }

  function renderCompactCard(cat: typeof CATS[number]) {
    const entry = data?.entries.find(e=>e.category===cat.key)
    return (
      <div key={cat.key} className="card" style={{background:'#0b1220'}}>
        {!isEditing[cat.key] ? (
          <div className="entry-card-summary">
            <div className="entry-card-summary-copy">
              <div className="entry-card-title">{cat.label}</div>
              <small className="muted">
                {entry
                  ? `Submitted${entry.title ? `: ${entry.title}` : '.'}`
                  : 'No details saved yet.'}
              </small>
            </div>
            <div className="entry-card-actions entry-card-actions-summary">
              {entry?.previewUrl ? (
                renderEntryPreview(cat.label, entry, 'inline')
              ) : (
                <span className="badge">no submission</span>
              )}
              {!data?.locked && (
                <button type="button" onClick={() => setIsEditing(prev => ({ ...prev, [cat.key]: true }))}>Edit</button>
              )}
            </div>
          </div>
        ) : (
          <>
            <div className="entry-card-header entry-card-header-open">
              <div className="entry-card-title">{cat.label}</div>
              <div className="entry-card-actions">
                {!entry?.previewUrl && <span className="badge">no submission</span>}
                {!data?.locked && entry && (
                  <button
                    type="button"
                    className="icon-button"
                    aria-label={`Collapse ${cat.label} editor`}
                    title="Collapse editor"
                    onClick={() => {
                      setConfirmDeleteByCat(prev => ({ ...prev, [cat.key]: false }))
                      setIsEditing(prev => ({ ...prev, [cat.key]: false }))
                    }}
                  >
                    ^
                  </button>
                )}
              </div>
            </div>
            {renderEntryFields(cat.key, entry)}
          </>
        )}
      </div>
    )
  }

  return (
    <div className="card">
      {err && <p className='error-text'>{err}</p>}
      {ok && <p><small className="muted">{ok}</small></p>}
      {!data ? <p><small className="muted">Loading…</small></p> : (
        <>
          <h2 style={{marginTop:0}}>{data.competition.title}</h2>
          <div className="row" style={{justifyContent:'space-between'}}>
            <div>
              <div className="row">
                <span className="badge">{data.submission.className}</span>
                <span className="badge">{data.submission.competitionNumber}</span>
              </div>
              <div style={{marginTop:8}}><small className="muted">Closes at: {new Date(data.competition.closesAt).toLocaleString()}</small></div>
              <div><small className="muted">Time remaining: {countdown.pretty}</small></div>
            </div>
            <span className="badge">{data.locked ? 'locked' : 'open'}</span>
          </div>

          <div className="card" style={{marginTop:12, background:'#0b1220'}}>
            <strong>Entry guidance</strong>
            <p style={{marginBottom:0, marginTop:10}}>{ENTRY_GUIDANCE}</p>
          </div>

          {data.locked && (
            <div className="card" style={{marginTop:12, background:'#0b1220'}}>
              <b>Submissions are locked.</b>
              <div><small className="muted">The competition has closed or judging is open.</small></div>
            </div>
          )}

          <hr />

          <div style={{display:'grid', gap: 12}}>
            {CATS.map(renderCompactCard)}
          </div>

          {firstArrivalEmail && (
            <>
              <hr />
              <div className="card" style={{background:'#0b1220'}}>
                <small className="muted">
                  If you need to return and edit your submissions before the competition closes, an email has been sent to {firstArrivalEmail} with your personal edit link.
                </small>
              </div>
            </>
          )}
        </>
      )}
    </div>
  )
}
